<script lang="ts">
  // WebGL field renderer (Three.js), the glowing 3D playmat + field cards. Hands + HUD stay in the
  // DOM (Duel.svelte) on top of this canvas. Hover/click come back from raycasting via callbacks.
  import { onMount, untrack } from "svelte";
  import { DuelArena, type CardState } from "../../three/DuelArena.ts";
  import { onMotion, setArenaRect, pickZone, openPile, duel } from "../../stores/duel.svelte.ts";
  import { settings } from "../../stores/settings.svelte.ts";
  import type { PlayerBoard, QueriedCard } from "@ygoevo/protocol";

  let {
    me,
    opp,
    seat,
    prompt,
    cardState,
    foils = {},
    links = new Set<string>(),
    sleeve = undefined,
    oppSleeve = undefined,
    onhover,
    onclick,
    onbackdrop,
    oninitfail,
  }: {
    me: PlayerBoard;
    opp: PlayerBoard;
    seat: number;
    /** Reactive trigger so the selectable glow/sword refresh when the prompt changes (value unused). */
    prompt: unknown;
    cardState: (c: QueriedCard) => CardState;
    /** Your owned foils (passcode → best grade), only YOUR face-up cards foil. */
    foils?: Record<number, number>;
    /** Keys ("ctrl|loc|seq") of cards in the hovered equip/target relationship group → link badges. */
    links?: Set<string>;
    /** Your chosen card-sleeve id (cosmetic card-back). */
    sleeve?: string;
    /** Opponent's sleeve id (their backs read as theirs). */
    oppSleeve?: string;
    onhover: (c: QueriedCard | null) => void;
    onclick: (c: QueriedCard, clientX: number, clientY: number) => void;
    /** Empty board clicked → dismiss the click-to-inspect card detail. */
    onbackdrop?: () => void;
    /** The 3D renderer failed/hung during init (no WebGPU) — parent should fall back to the 2D board. */
    oninitfail?: () => void;
  } = $props();

  let canvas: HTMLCanvasElement;
  // Reactive so the board $effect re-runs once onMount has created the arena (otherwise the effect
  // fires once while arena is still null and, with a static initial board, never refreshes).
  let arena = $state<DuelArena | null>(null);

  onMount(() => {
    // Free Duel shows the Extra Monster (Link) Zones; World duels keep the classic 5-zone field.
    arena = new DuelArena(canvas, { onHover: onhover, onClick: onclick, onZoneClick: pickZone, onPileClick: openPile, onBackdrop: () => onbackdrop?.(), onInitFail: () => oninitfail?.() }, duel.format === "free");
    if (import.meta.env.DEV) (window as unknown as { __arena?: DuelArena }).__arena = arena; // dev console hook
    if (sleeve) arena.setSleeve(sleeve);
    if (oppSleeve) arena.setOppSleeve(oppSleeve);
    arena.setFoils((code) => foils[code] ?? 0);
    // board events → in-arena flourishes (summon flash / flip / attack lunge / activation surge)
    const offMotion = onMotion((m) => arena?.motion(m));
    // expose world→screen zone projection so the DOM motion layer can fly cards over the 3D board
    setArenaRect((c, l, s) => arena?.screenRectFor(c, l, s) ?? null);
    const onResize = () => arena?.resize();
    window.addEventListener("resize", onResize);
    // Also observe the canvas itself: onMount can fire before the flex layout has given the canvas its
    // real size (it came up as a 2×2 buffer → black board until the first window resize). A
    // ResizeObserver re-sizes the arena the moment layout settles, and on every container reflow after.
    const ro = new ResizeObserver(() => arena?.resize());
    ro.observe(canvas);
    return () => {
      offMotion();
      setArenaRect(null);
      window.removeEventListener("resize", onResize);
      ro.disconnect();
      arena?.dispose();
      arena = null;
    };
  });

  // Graphics-quality change (manual pick or the auto monitor) → re-solve the render resolution live
  // (resize() reads the effective tier's DPR cap; the loop reads its FPS cap each frame).
  $effect(() => { settings.renderQuality; settings.autoTier; arena?.resize(); });
  // keep sleeves in sync if they change mid-session
  $effect(() => { if (sleeve) arena?.setSleeve(sleeve); });
  $effect(() => { if (oppSleeve) arena?.setOppSleeve(oppSleeve); });
  // light up the legal zones whenever a manual placement is pending (auto-zone off)
  $effect(() => { arena?.setPlaceTargets(duel.placePick ? duel.placePick.targets : null); });
  // mark zones a continuous effect (Ground Collapse) has shut off with a padlock + hazard fill
  $effect(() => { arena?.setDisabledZones(duel.disabledZones); });
  // attack-aim arc: from the declared attacker to whichever legal target the pointer is over
  $effect(() => { arena?.setAim(duel.aimFrom ?? null, duel.cardPick ? duel.cardPick.targets : []); });
  // equip/target relationship badges, light up the hovered card's linked group (recomputed on hover)
  $effect(() => { arena?.setLinks(links); });
  // numbered chain-link badges floating over each activating card (pushed/popped LIFO by the store).
  // The hand is now rendered IN the WebGL scene (the fan), so hand links get a canvas badge anchored to
  // the fan card too — no cards are excluded.
  $effect(() => {
    arena?.setChainLinks(duel.chainLinks
      .map((c) => ({ link: c.link, controller: c.controller, location: c.location, sequence: c.sequence, code: c.code })));
  });
  // your foils arrive async (InventoryGet round-trip), when they land, re-evaluate the board so
  // your face-up cards pick up their holographic finish.
  $effect(() => { const f = foils; arena?.setFoils((code) => f[code] ?? 0); arena?.refreshFoils(); });

  // Rebuild the field on board OR prompt change. `cardState` is untracked so the cosmetic per-second
  // turn timer (which re-renders the parent) doesn't thrash a full scene rebuild.
  $effect(() => {
    void prompt;
    void duel.cardPick;   // refresh candidate glows when an on-board (attack/effect) pick begins or ends
    void duel.targeted;   // light the magenta target glow when an effect picks a card (and clear it after)
    const m = me, o = opp, s = seat;
    if (arena && m && o) arena.setBoard(m, o, s, untrack(() => cardState));
  });
</script>

<canvas bind:this={canvas} class="absolute inset-0 block size-full"></canvas>
